// Body of the "A/B Test" accordion on the A/B Testing campaign page: arms and weights, goal,
// lifecycle buttons and results. The decisions live in utils/abTesting.js (unit-tested).
// Laid out like the other campaign types' Targeting & Content accordion: a toolbar, the flush
// house table, then the settings, lifecycle and results blocks (styles in abTesting.css).
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { showToast as toast } from '../../toastManager';
import {
    AB_SERVER_OWNED_FIELDS, abEditingNote, abSxiContent, armKey, mergeLifecycleAbTest, equalArms, fromPercent, lifecycleActions, nextArmLetter, publishesBeforeStatus, toPercent,
    variantDefined, variantRemovable, weightsValid,
} from '../../utils/abTesting';
import { abApi } from '../../utils/abApi';
import ABResultsPanel from './ABResultsPanel';
import { CopyIcon, EyeIcon, PencilIcon, PlusIcon, TrashIcon } from './abIcons';
import './abTesting.css';

const parseSelectors = (text) => text.split('\n').map((s) => s.trim()).filter(Boolean);

const lifecycleDiffers = (ab, out) => !!out?.campaign_status
    || AB_SERVER_OWNED_FIELDS.some((field) => (out?.ab_test?.[field] ?? null) !== (ab[field] ?? null));

export default function ABTestSection({ app_url, campaignData, saveData, templateLocked, allTargeting }) {
    const navigate = useNavigate();
    const [busy, setBusy] = useState(false);
    const ab = campaignData.ab_test || {};
    const variants = campaignData.targeting_and_content || [];
    const arms = ab.arms || [];
    const actions = lifecycleActions(ab, variants);
    const landing = campaignData.landing_page_urls;

    // Selectors are edited as raw text and parsed on blur, so a new empty line can be typed.
    const [goalText, setGoalText] = useState((ab.goal_selectors || []).join('\n'));
    useEffect(() => {
        setGoalText((ab.goal_selectors || []).join('\n'));
    }, [campaignData.campaign_id]); // eslint-disable-line react-hooks/exhaustive-deps

    const weightOf = (key) => arms.find((a) => a.key === key)?.weight ?? 0;
    const commit = (next) => saveData(null, next);   // the page's session copy + state, like every other edit
    const updateAb = (patch) => commit({ ...campaignData, ab_test: { ...ab, ...patch } });

    const addVariant = async () => {
        const letter = nextArmLetter(variants);
        if (!letter) {
            toast.error('A test can have at most 26 variants.');
            return;
        }
        try {
            const key = await abSxiContent(landing, campaignData.campaign_id, letter);
            const row = {
                id: uuidv4(), name: `Variant ${letter.toUpperCase()}`, status: 'enabled', ab_arm: armKey(letter),
                targeting: [key], value: null, lpFinalUrl: `${landing}?sxi_content=${key}`,
            };
            const nextVariants = [...variants, row];
            commit({ ...campaignData, targeting_and_content: nextVariants, ab_test: { ...ab, arms: equalArms(nextVariants) } });
        } catch (e) {
            toast.error(e.message);
        }
    };

    const removeVariant = async (row) => {
        if (!window.confirm(`Remove ${row.name}?`)) return;
        // Its arm goes too; the other arms keep their weights (the weights check flags a sum off 100%).
        const next = {
            ...campaignData,
            targeting_and_content: variants.filter((v) => v.id !== row.id),
            ab_test: { ...ab, arms: arms.filter((a) => a.key !== row.ab_arm) },
        };
        try {
            if (variantDefined(row)) {
                // Its content was published: remove it server-side too, which rewrites the page's flags.
                await axios.post(`${app_url}/api/cux/delete-variant`,
                    { campaign_id: campaignData.campaign_id, variant_id: row.id, landing_page_url: landing },
                    { withCredentials: true });
            }
            commit(next);
        } catch (e) {
            toast.error('Could not remove the variant.');
            return;
        }
        // Store it at once: delete-variant drops the row but not its arm, and Create and Save read the stored arms.
        try {
            await persist(next);
        } catch (e) {
            toast.error('The variant was removed, but the campaign could not be saved. Press Save.');
        }
    };

    const openEditor = (row) => navigate('/ab-test-variant', {
        state: { campaignData, targetingItem: row, previousPage: 'ab-testing-campaign', allTargeting },
    });

    // Lifecycle calls read the stored campaign, so publish the current state first.
    const persist = (data = campaignData) => axios.post(`${app_url}/api/cux/publish-campaign`, { campaignData: data }, { withCredentials: true });

    // A lifecycle response's server-owned ab_test fields and campaign status, merged into the page's campaign.
    const mergeLifecycle = (out) => commit({
        ...campaignData,
        ...(out?.campaign_status ? { status: out.campaign_status } : {}),
        ab_test: mergeLifecycleAbTest(ab, out?.ab_test),
    });

    const run = async (successMessage, call) => {
        setBusy(true);
        try {
            const out = await call();
            mergeLifecycle(out);
            toast.success(successMessage);
        } catch (e) {
            toast.error(e.message);
        } finally {
            setBusy(false);
        }
    };

    // The worker is authoritative, and its status can change outside ScaleX (the ops dashboard, or a
    // save that failed after a transition). get() brings the stored campaign back in line; pick it up.
    useEffect(() => {
        if (!ab.experiment_id) return undefined;
        let cancelled = false;
        abApi.get(app_url, campaignData.campaign_id)
            .then((out) => {
                // Only when the lifecycle differs: a no-op merge would still revert unsaved edits.
                if (!cancelled && lifecycleDiffers(ab, out)) mergeLifecycle(out);
            })
            .catch((e) => console.warn('[A/B] could not refresh the experiment status:', e.message));
        return () => { cancelled = true; };
    }, [ab.experiment_id]); // eslint-disable-line react-hooks/exhaustive-deps

    const createExperiment = () => run('Experiment created', async () => {
        await persist();
        return abApi.create(app_url, campaignData.campaign_id);
    });
    const saveChanges = () => run('Experiment updated', async () => {
        await persist();
        return abApi.update(app_url, campaignData.campaign_id);
    });
    const changeStatus = (status, message) => {
        if (status === 'completed'
            && !window.confirm('Complete this test? Everyone will see the page as authored again, and it cannot be restarted.')) return;
        run(message, async () => {
            // Start and Resume lock weights and split again: publish what the page shows first.
            if (publishesBeforeStatus(ab, status)) await persist();
            return abApi.setStatus(app_url, campaignData.campaign_id, status);
        });
    };

    const copyKey = async (key) => {
        try {
            await navigator.clipboard.writeText(key);
            toast.success('Copied');
        } catch (e) {
            toast.error('Could not copy. Select the key and copy it instead.');
        }
    };

    const weightInput = (key) => (
        <div className="ab-weight">
            <input
                type="number" min="0" max="100" step="0.01" className="form-control"
                aria-label={`Weight for ${key}`} value={toPercent(weightOf(key))} disabled={!actions.editable}
                onChange={(e) => updateAb({ arms: arms.map((a) => (a.key === key ? { ...a, weight: fromPercent(e.target.value) } : a)) })}
            />
            <span aria-hidden="true">%</span>
        </div>
    );

    const totalPercent = toPercent(arms.reduce((sum, a) => sum + (Number(a.weight) || 0), 0));
    const weightsOk = variants.length === 0 || weightsValid(arms.map((a) => a.weight));
    const cell = 'channel_performance_table_cell';

    // Edit, Create or View: all open the variant editor, which is read-only while the test runs.
    const editorLabel = (row) => (!actions.editable ? 'View' : (variantDefined(row) ? 'Edit' : 'Create'));

    return (
        <div className="ab-test" data-testid="ab-test-section">
            <div className="ab-toolbar">
                <div className="ab-actions">
                    {actions.editable && (
                        <>
                            <button type="button" className="btn sxi_primary_blue_btn btn-height"
                                disabled={!templateLocked || busy} onClick={addVariant}>
                                <PlusIcon /> Add variant
                            </button>
                            <button type="button" className="btn ab-btn-secondary btn-height" disabled={variants.length === 0}
                                onClick={() => updateAb({ arms: equalArms(variants) })}>Equalize weights</button>
                        </>
                    )}
                    {!templateLocked && <span className="ab-note mt-0">Select a landing page and a template first.</span>}
                </div>
                <span className={`ab-total${weightsOk ? '' : ' ab-total--invalid'}`}>
                    Total weight: {totalPercent}%{!weightsOk && ' · must add up to 100%'}
                </span>
            </div>

            <div className="personalization-targeting-table">
                <table className="ab-table">
                    <thead>
                        <tr>
                            <th className="channel_performance_header_cell">Name</th>
                            <th className="channel_performance_header_cell">Content</th>
                            <th className="channel_performance_header_cell">sxi_content</th>
                            <th className="channel_performance_header_cell">Weight</th>
                            <th className="channel_performance_header_cell">Preview</th>
                            <th className="channel_performance_header_cell">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr className="channel_performance_table_row">
                            <td className={cell}>
                                <div className="ab-name">Control</div>
                                <div className="ab-sub">Page as authored</div>
                            </td>
                            <td className={cell}>—</td>
                            <td className={cell}>—</td>
                            <td className={cell}>{weightInput('control')}</td>
                            <td className={cell}>—</td>
                            <td className={cell}>—</td>
                        </tr>
                        {variants.map((row) => {
                            const defined = variantDefined(row);
                            const key = row.targeting?.[0] || '';
                            return (
                                <tr key={row.id} className="channel_performance_table_row">
                                    <td className={cell}>
                                        <div className="ab-name">{row.name}</div>
                                        <div className="ab-sub">{row.ab_arm}</div>
                                    </td>
                                    <td className={cell}>
                                        <span className={`sxi-status-badge sxi-status-badge--${defined ? 'enabled' : 'draft'}`}>
                                            <span className="sxi-status-badge__dot" />
                                            <span className="sxi-status-badge__text">{defined ? 'Defined' : 'Not defined'}</span>
                                        </span>
                                    </td>
                                    <td className={cell}>
                                        <div className="d-flex align-items-center gap-1">
                                            <span className="ab-key">{key}</span>
                                            <button type="button" className="ab-icon-btn" onClick={() => copyKey(key)}
                                                aria-label={`Copy sxi_content of ${row.name}`} title="Copy">
                                                <CopyIcon />
                                            </button>
                                        </div>
                                    </td>
                                    <td className={cell}>{weightInput(row.ab_arm)}</td>
                                    <td className={cell}>
                                        {defined ? (
                                            <a className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1"
                                                href={`${landing}?sxi_content=${key}`} target="_blank" rel="noreferrer">
                                                <EyeIcon /> Preview
                                            </a>
                                        ) : '—'}
                                    </td>
                                    <td className={cell}>
                                        <div className="d-flex align-items-center gap-1">
                                            <button type="button" className="ab-icon-btn" disabled={!templateLocked}
                                                onClick={() => openEditor(row)} aria-label={editorLabel(row)} title={editorLabel(row)}>
                                                {actions.editable ? <PencilIcon /> : <EyeIcon />}
                                            </button>
                                            {variantRemovable(ab, row) && (
                                                <button type="button" className="ab-icon-btn" onClick={() => removeVariant(row)}
                                                    aria-label="Remove" title="Remove">
                                                    <TrashIcon />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="ab-block">
                <h6 className="ab-section-title">Goal &amp; traffic</h6>
                <div className="row g-3">
                    <div className="col-md-6 ab-field">
                        <label className="ab-field-label" htmlFor="ab-traffic">Traffic in test (%)</label>
                        <input id="ab-traffic" type="number" min="1" max="100" className="form-control"
                            value={Math.round((ab.traffic_split ?? 1) * 100)} disabled={!actions.editable}
                            onChange={(e) => updateAb({ traffic_split: Math.min(1, Math.max(0.01, (Number(e.target.value) || 0) / 100)) })} />
                        <p className="ab-help">Share of visitors entered into the test.</p>
                    </div>
                    <div className="col-md-6 ab-field">
                        <label className="ab-field-label" htmlFor="ab-goal-name">Goal name</label>
                        <input id="ab-goal-name" type="text" className="form-control" placeholder="e.g. apply_click"
                            value={ab.goal_metric || ''} disabled={ab.status === 'completed'}
                            onChange={(e) => updateAb({ goal_metric: e.target.value })} />
                    </div>
                    <div className="col-md-6 ab-field">
                        <label className="ab-field-label" htmlFor="ab-goal-selectors">Goal elements (CSS selectors, one per line)</label>
                        <textarea id="ab-goal-selectors" rows={3} className="form-control" placeholder="#apply-now"
                            value={goalText} disabled={ab.status === 'completed'}
                            onChange={(e) => setGoalText(e.target.value)}
                            onBlur={() => updateAb({ goal_selectors: parseSelectors(goalText) })} />
                        <p className="ab-help">Prefer buttons: a click on a link that leaves the page can be lost.</p>
                    </div>
                    <div className="col-md-6 ab-field">
                        <label className="ab-field-label" htmlFor="ab-hypothesis">Hypothesis (optional)</label>
                        <textarea id="ab-hypothesis" rows={3} className="form-control"
                            value={ab.hypothesis || ''} disabled={ab.status === 'completed'}
                            onChange={(e) => updateAb({ hypothesis: e.target.value })} />
                    </div>
                </div>
            </div>

            {(actions.create || actions.save || actions.start || actions.resume || actions.complete || abEditingNote(ab)) && (
                <div className="ab-block">
                    <div className="ab-actions">
                        {actions.create && (
                            <button type="button" className="btn sxi_primary_blue_btn btn-height"
                                disabled={busy || !actions.createEnabled || !templateLocked} onClick={createExperiment}>Create experiment</button>
                        )}
                        {actions.start && (
                            <button type="button" className="btn sxi_primary_blue_btn btn-height" disabled={busy}
                                onClick={() => changeStatus('running', 'Experiment started')}>Start</button>
                        )}
                        {actions.resume && (
                            <button type="button" className="btn sxi_primary_blue_btn btn-height" disabled={busy}
                                onClick={() => changeStatus('running', 'Experiment resumed')}>Resume</button>
                        )}
                        {actions.save && (
                            <button type="button" className="btn ab-btn-secondary btn-height" disabled={busy} onClick={saveChanges}>Save changes</button>
                        )}
                        {actions.pause && (
                            <button type="button" className="btn ab-btn-secondary btn-height" disabled={busy}
                                onClick={() => changeStatus('paused', 'Experiment paused')}>Pause</button>
                        )}
                        {actions.complete && (
                            <button type="button" className="btn ab-btn-secondary btn-height" disabled={busy}
                                onClick={() => changeStatus('completed', 'Experiment completed')}>Complete</button>
                        )}
                        {busy && <span className="spinner-border spinner-border-sm text-secondary" role="status" aria-label="Working" />}
                    </div>
                    {abEditingNote(ab) && <p className="ab-note">{abEditingNote(ab)}</p>}
                    {actions.create && !actions.createEnabled && (
                        <p className="ab-note">To create the experiment, add at least one variant, give every variant content, and make the weights add up to 100%.</p>
                    )}
                </div>
            )}

            {ab.experiment_id && ab.status !== 'draft' && (
                <ABResultsPanel app_url={app_url} campaignId={campaignData.campaign_id} arms={arms} variants={variants} />
            )}
        </div>
    );
}
